<script setup>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { reactive, watch, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedRecord: {
    type: Object,
    default: () => ({}),
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const formData = reactive({
  name: '',
});

const isSubmitting = ref(false);

const rules = {
  name: { required },
};

const v$ = useVuelidate(rules, formData);

watch(
  () => props.selectedRecord,
  newRecord => {
    if (props.isEdit && newRecord) {
      formData.name = newRecord.name;
    }
  },
  { immediate: true, deep: true }
);

const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  try {
    const action = props.isEdit ? 'pipelines/update' : 'pipelines/create';
    const payload = props.isEdit
      ? {
          id: props.selectedRecord.id,
          ...formData,
        }
      : formData;
    await store.dispatch(action, payload);
    useAlert(
      props.isEdit
        ? t('CRM.PIPELINES.SUCCESS_UPDATE')
        : t('CRM.PIPELINES.SUCCESS_CREATE')
    );
    emit('close');
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col pt-8">
    <woot-modal-header
      :header-title="
        isEdit
          ? $t('CRM.PIPELINES.UPDATE_TITLE')
          : $t('CRM.PIPELINES.CREATE_TITLE')
      "
    />
    <form class="flex flex-col w-full" @submit.prevent="submitForm">
      <div class="w-full px-8 pb-8">
        <label>
          {{ $t('CRM.PIPELINES.NAME') }}
          <input
            v-model.trim="formData.name"
            type="text"
            :placeholder="$t('CRM.PIPELINES.NAME_PLACEHOLDER')"
            @input="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="message text-n-ruby-11">
            {{ $t('CRM.PIPELINES.NAME_REQUIRED') }}
          </span>
        </label>

        <div class="flex justify-end gap-2 mt-8">
          <Button variant="ghost" type="button" @click.prevent="$emit('close')">
            {{ $t('CRM.FORM.CANCEL') }}
          </Button>
          <Button :is-loading="isSubmitting" type="submit">
            {{
              isEdit
                ? $t('CRM.PIPELINES.UPDATE_SUBMIT')
                : $t('CRM.PIPELINES.CREATE_SUBMIT')
            }}
          </Button>
        </div>
      </div>
    </form>
  </div>
</template>
